import type { ServicePageData } from './types';

const projectingSigns: ServicePageData = {
  slug: 'projecting-signs',
  eyebrow: 'Signage',
  title: 'Be seen from every angle with',
  titleAccent: 'projecting signs.',
  intro:
    'Wall-mounted brackets and customisable panels, an excellent way to create signage visible from all angles. Mix and match your bracket and panel, then chat to our design team who will customise your sign to promote your business in the most eye-catching way.',
  heroImages: [
    '/home/projecting-signs/projecting-sign-hero.jpg',
    '/home/projecting-signs/projecting-sign-petalbar.jpg',
    '/home/projecting-signs/projecting-sign-scooch-coffee.jpg',
    '/home/projecting-signs/projecting-sign-kearns-and-rouse.jpg',
    '/home/projecting-signs/blade-sign-1.jpg',
    '/home/projecting-signs/projecting-sign-petalbar-2.jpg',
    '/home/projecting-signs/projecting-sign-install.jpg',
    '/home/projecting-signs/round-illuminated-projecting-sign.jpg',
    '/home/projecting-signs/projecting-sign-scooch-coffee-2.jpg',
    '/home/projecting-signs/traditional-framed-rigid-sign.jpg',
    '/home/projecting-signs/projecting-sign-kearns-and-rouse-2.jpg',
    '/home/projecting-signs/dark-green-double-fullframe.jpg',
    '/home/projecting-signs/projecting-sign-petalbar.jpg',
    '/home/projecting-signs/blade-sign-2.jpg',
    '/home/projecting-signs/projecting-sign-scooch-coffee.jpg',
    '/home/projecting-signs/projecting-sign-hero.jpg',
    '/home/projecting-signs/aluminium-frame-swing-panel-2.jpg',
    '/home/projecting-signs/projecting-sign-kearns-and-rouse.jpg',
    '/home/projecting-signs/projecting-sign-install.jpg',
    '/home/projecting-signs/projecting-sign-petalbar-2.jpg',
    '/home/projecting-signs/projecting-sign-scooch-coffee-2.jpg',
  ],

  useCases: [
    { title: 'Cafes and coffee shops', text: 'Catch people walking along the pavement, not just those opposite.' },
    { title: 'Salons and barbers', text: 'A double-sided panel that reads from both directions of the street.' },
    { title: 'Bars and restaurants', text: 'Traditional swing signs that suit period frontages.' },
    { title: 'Listed buildings', text: 'Framed rigid signs designed to keep the planners happy.' },
    { title: 'Trading after dark', text: 'Illuminated round signs visible at all times of day and night.' },
  ],

  optionsHeading: 'Choose your bracket',
  optionsIntro:
    'Our brackets come in a range of styles and sizes. Mix and match one of our bracket styles with one of our standard panel sizes, then add customisation.',
  options: [
    {
      id: 'blade-flange',
      title: 'Blade / flange sign',
      lead: 'From £52 + VAT. Our cheapest projecting sign solution.',
      body: 'An all-in-one projecting sign, just add decals. Laser cut from a single sheet of aluminium with a fixing plate that screws straight to the wall, so there is no separate bracket or panel to buy.',
      image: '/home/projecting-signs/blade-sign-1.jpg',
      specs: [
        { label: 'Sizes', value: 'Circular 47.5 cm. Square 40 or 50 cm. Oval 40 × 55 cm portrait or landscape. Rectangle 55 × 40 cm landscape.' },
        { label: 'Material', value: 'Laser cut from 2 mm thick aluminium with a 50 × 200 mm fixing plate.' },
        { label: 'Projection', value: 'Stands off the wall by 50 mm.' },
        { label: 'Colour', value: 'Powder coated white. Can be wrapped in a range of vinyl colours and finishes.' },
      ],
    },
    {
      id: 'modern-swing',
      title: 'Modern swing sign bracket',
      lead: 'From £105 + VAT. Traditional bracket styles also available on request.',
      body: 'A versatile bracket that works with aluminium composite panels, traditional aluminium framed panels and PVC foam panels. Heavy duty steel construction, zinc plated and powder coated.',
      image: '/home/projecting-signs/slim-modern-projecting-sign-bracket.jpg',
      specs: [
        { label: 'Lengths', value: '60, 75, 85, 100 or 120 cm.' },
        { label: 'Colours', value: 'Black or white as standard. Other stock colours available, or custom colours in any RAL shade.' },
        { label: 'Fixings', value: 'Supplied with free wall fixings and hanger bolts.' },
        { label: 'Works with', value: 'Aluminium composite, traditional aluminium framed and PVC foam panels.' },
      ],
    },
    {
      id: 'rigid-projecting',
      title: 'Rigid projecting sign',
      lead: 'From £110 + VAT. A fixed panel that will not swing.',
      body: 'Works with aluminium composite panels for a solid, professional appearance. Heavy duty steel construction, zinc plated and powder coated.',
      image: '/home/projecting-signs/rigid-sign-bracket.jpg',
      specs: [
        { label: 'Square sizes', value: '30, 40, 45, 50, 55, 60, 70, 75 or 80 cm.' },
        { label: 'Rectangle sizes', value: '60 × 40, 90 × 60 or 115 × 55 cm, portrait or landscape.' },
        { label: 'Colours', value: 'Black or white as standard. Other stock colours available, or custom colours in any RAL shade.' },
        { label: 'Fixings', value: 'Supplied with free wall fixings.' },
      ],
    },
    {
      id: 'framed-rigid',
      title: 'Framed rigid sign',
      lead: 'Contact us for a quote. Made to measure for period frontages.',
      body: 'A copy of an old style traditional wooden frame but in long-lasting aluminium. This style is more in keeping with the character of listed properties and is designed to help keep the planners happy.',
      image: '/home/projecting-signs/traditional-framed-rigid-sign.jpg',
      specs: [
        { label: 'Size', value: 'Custom made to any size.' },
        { label: 'Frame', value: '55 × 65 mm aluminium frame designed to take a 10 mm thick panel. Black as standard, other RAL colours available.' },
        { label: 'Fixings', value: 'Supplied with free wall mounting brackets and ankerbolt fixings.' },
        { label: 'Works with', value: 'PVC foam panels.' },
      ],
    },
    {
      id: 'illuminated-round',
      title: 'Rigid illuminated round projecting sign',
      lead: 'Contact us for a price. Visible at all times of day and night.',
      body: 'Make sure your sign is seen, especially during the darker months of the year. An all-in-one light box with pre-installed energy-saving LED modules that illuminate the whole sign. Just add decals.',
      image: '/home/projecting-signs/round-illuminated-projecting-sign.jpg',
      specs: [
        { label: 'Size', value: '80 × 80 cm face. Overall 80 × 14 × 94 cm.' },
        { label: 'Construction', value: 'Aluminium frame with 3 mm acrylic face panels on both sides.' },
        { label: 'Colours', value: 'Black or silver as standard.' },
        { label: 'Wiring', value: 'Supplied pre-wired. Must be installed or fitted with a plug by an electrician. Free wall fixings included.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Printed vinyl',
      text: 'Inks are used to print a design onto clear or white vinyl. Ideal for multiple colours, gradients, shading, photographs and complex illustrations. Because the ink sits on the surface, colour can fade quicker than cut vinyl.',
    },
    {
      title: 'Cut vinyl',
      text: 'A specialist machine follows the design with a sharp blade, cutting shapes and letters from pre-coloured rolls of vinyl. Sharper edges than print, with no background. Best for simple designs, logos and lettering.',
    },
  ],

  gallery: [
    '/home/projecting-signs/projecting-sign-hero.jpg',
    '/home/projecting-signs/projecting-sign-petalbar.jpg',
    '/home/projecting-signs/projecting-sign-scooch-coffee.jpg',
    '/home/projecting-signs/projecting-sign-kearns-and-rouse.jpg',
    '/home/projecting-signs/projecting-sign-petalbar-2.jpg',
    '/home/projecting-signs/projecting-sign-scooch-coffee-2.jpg',
    '/home/projecting-signs/projecting-sign-kearns-and-rouse-2.jpg',
    '/home/projecting-signs/projecting-sign-install.jpg',
    '/home/projecting-signs/blade-sign-2.jpg',
  ],

  steps: [
    {
      title: 'Get a free quote',
      text: 'Mix and match one of our bracket styles with one of our standard panel sizes, then add customisation.',
    },
    {
      title: 'Design stage',
      text: 'Already have artwork? Send it over for a proof. Need help? Send your logo and our design team will put together a visual of how your projecting sign could look on your premises.',
    },
    {
      title: 'Approve your order',
      text: 'After you approve your order we get it into production, which usually takes 5 to 10 working days. You can also book our installation team to fit it.',
    },
    {
      title: 'Order up',
      text: 'Collect from our shop in Kidderminster, have it posted to you from £10, or our fully insured installation team will meet you on site to fit your sign.',
    },
  ],

  info: [
    {
      title: 'Choose your sign panel',
      text: 'Our projecting sign panels come in a range of styles and sizes. Already have a bracket and just need to switch out the panels? Give us a couple of measurements and we will make sure the new panel fits.',
      bullets: [
        'Aluminium composite: 3 mm, double sided, cut to size, pre-drilled with fixings. White as standard, wrapped in any vinyl colour or sprayed in any RAL colour',
        'PVC foam: 10, 19 or 24 mm thick, square or bullnose edge, lightweight',
        'Traditional frame with panel: made-to-measure aluminium frame with a white 10 mm PVC foam panel, black as standard or powder coated in any RAL colour',
      ],
    },
    {
      title: 'Our installation team',
      text: 'Our professional install team has years of experience installing signage and is available to fit projecting signs throughout the Midlands.',
      bullets: [
        'All access equipment, scaffolding towers and platforms organised',
        'Fully insured, health and safety and first aid trained',
        'Risk assessments supplied on request',
      ],
    },
    {
      title: 'Supply only',
      text: 'Want to install your signage yourself? We can supply your bracket and panels with all the fixings you will need.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10', 'Production usually 5 to 10 working days from approval'],
    },
  ],

  cta: {
    title: 'Get your free projecting sign quote.',
    text: 'Contact us for advice on selecting your bracket, panel and customisation style, design, installation or any other questions you may have.',
    image: '/home/projecting-signs/projecting-sign-install.jpg',
  },
};

export default projectingSigns;
